import { Component, OnDestroy, OnInit } from '@angular/core';
import {DateTime} from 'luxon';
import { AppointmentService } from '../../../shared/services/appointment.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SubscriptionList } from '../../../shared/models/asp.types';
import { getTradeInVehicleSummaryText, unsubscribeSubscriptions } from '../../../shared/services/util.service';
import { AppointmentType, IResponse, ISalesAppointment, LocationType } from '@signal/asp-data-commons';
import { AspBannerService } from '../../../shared/services/asp-banner.service';
import { IVehicleInquiryAppointment } from '@signal/asp-data-commons/lib/interfaces/appointment';
import { VehicleType, VehiclesOfInterest } from '@signal/asp-data-commons/lib/enums/enum';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-confirm-info',
  templateUrl: './confirm-info.component.html',
  styleUrls: ['./confirm-info.component.scss']
})
export class ConfirmInfoComponent implements OnInit, OnDestroy {
  view = 'confirm';
  appointment: IVehicleInquiryAppointment;
  makeModelYear: string;
  detailMap: { [key: string]: string };
  id: string;
  subscriptions: SubscriptionList = {};

  constructor(
    private readonly appointmentService: AppointmentService,
    private readonly route: ActivatedRoute,
    private readonly bannerService:AspBannerService,
    private readonly router: Router,
    private readonly translateService:TranslateService) {

    const urlState = this.router.getCurrentNavigation()?.extras?.state as ISalesAppointment;
    if(urlState?.appointmentId) {
      this.appointment = urlState;
      this.setDetailMap(urlState);
    } else {
      this.subscriptions.params = this.route.queryParams.subscribe(params => {
        this.id = params.id;
        if (this.id) {
          this.subscriptions.getAppt = this.appointmentService.get(AppointmentType.VEHICLE_INQUIRY, this.id).subscribe((res: IResponse<IVehicleInquiryAppointment>) => {
            if (res && res.data) {
              this.appointment = res.data;
              this.setDetailMap(res.data);
            }
          });
        }
      });
    }
  }

  ngOnInit(): void {
    this.bannerService.hide();
  }

  setDetailMap(appointmentDetails) {
    const isRemoteLocation = appointmentDetails.transportation.category === LocationType.REMOTE;

    const date = `${DateTime.fromISO(appointmentDetails.appointmentStartDate).toFormat('MMMM dd')}`;
    const time = appointmentDetails.timeSlotId;

    const dateTime = `${date} at ${time}`;

    const advisorName = appointmentDetails.advisor?.displayName ?? '';
    const commentsFromDealer = appointmentDetails.appointmentComments ? this.getComment(appointmentDetails.appointmentComments, 'Comments-From-Dealer') : '';
    const dealerInternalComment =  appointmentDetails.appointmentComments ? this.getComment(appointmentDetails.appointmentComments, 'Dealer-Internal-Comments') : '';
    const additionalComments = appointmentDetails.appointmentComments ? this.getComment(appointmentDetails.appointmentComments, 'Customer-Additional-Comments') : '';

    let customerAddress = '';
    if (isRemoteLocation) {
      const custAdd = appointmentDetails.dropOffAddress;
      customerAddress = `otherTexts.weWillBringTheCarTo:\n${custAdd.addressLine1}, ${custAdd.addressLine2 ? custAdd.addressLine2 + ', ' : ''} ${custAdd.city}, ${custAdd.state} - ${custAdd.zip}`;
    }

    let vehicleOfInterestSummaryArr = [];
    if(appointmentDetails?.vehicleType) {
      vehicleOfInterestSummaryArr = (appointmentDetails.vehicleType === VehicleType.BOTH)? [this.translateService.instant('vehicleInquiry.new'), this.translateService.instant('vehicleInquiry.used')] : [this.translateService.instant(`vehicleInquiry.${appointmentDetails.vehicleType.toLowerCase()}`)];
    }
 
    appointmentDetails?.vehiclesOfInterest?.forEach(inqVehicle=>{
      if(inqVehicle ===  VehiclesOfInterest.CARS_MINIVAN) {
        vehicleOfInterestSummaryArr.push( this.translateService.instant('vehicleInquiry.carsAndMinivan'))
      } else if(inqVehicle ===  VehiclesOfInterest.CROSSOVERS_SUVS) {
        vehicleOfInterestSummaryArr.push(this.translateService.instant('vehicleInquiry.crossoversAndSUVs'))
      } else if(inqVehicle ===  VehiclesOfInterest.ELECTRIFIED) {
        vehicleOfInterestSummaryArr.push(this.translateService.instant('vehicleInquiry.electrified'))
      } else if(inqVehicle ===  VehiclesOfInterest.TRUCKS) {
        vehicleOfInterestSummaryArr.push(this.translateService.instant('vehicleInquiry.trucks'))
      }
    });
    
    const vehiclesOfInterest = vehicleOfInterestSummaryArr.join(', ');

    const dealershipAddress = `${appointmentDetails.dealer.addressShort}`;

    this.detailMap = {
      'service.time': dateTime,
      'serviceSchedule.consultant': advisorName,
      'vehicleInquiry.vehiclesOfInterest': vehiclesOfInterest,
      'delivery.commentsFromDealer': commentsFromDealer,
      'delivery.internalDealerComments': dealerInternalComment,
      'delivery.customerComments': additionalComments,
      'service.location': `${(isRemoteLocation ? customerAddress : dealershipAddress)}`
    };
    if(!vehiclesOfInterest) {
      this.detailMap['vehicleInquiry.vehiclesOfInterest'] = this.translateService.instant('vehicleInquiry.notSpecified');
    }
    if(!commentsFromDealer) {
      delete this.detailMap['delivery.commentsFromDealer']
    }
    if(!dealerInternalComment) {
      delete this.detailMap['delivery.internalDealerComments']
    }
    if(!additionalComments) {
      delete this.detailMap['delivery.customerComments']
    }

    const tradeInSummary = getTradeInVehicleSummaryText((appointmentDetails as any).tradeInVehicle);
    if (tradeInSummary) {
      this.detailMap['testDrive.tradeIn'] = tradeInSummary;
    }
  }

  getComment(data, field) {
    let returnComment = '';
    data.split(':::').forEach(comment=> {
      if(comment.includes(field)) {
        returnComment = comment.replace(`${field} - `, '').trim();
      }
    })
    return returnComment;
  }

  ngOnDestroy(): void {
    unsubscribeSubscriptions(this.subscriptions);
  }

}